import React from 'react';
import { Button, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../app/store';
import { openPricingPageOrSubscriptionPortal } from '../../services/subscription';
import { Notify } from '../../services/notificationService';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { getCreditButtonInfo } from '../../utils/creditUtils';
import { useAbility } from '../../hooks/useAbility';

interface CreditButtonProps {
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Consolidated credit button component that handles:
 * - Free plan users: "Activate subscription to purchase more" → pricing page
 * - Trial users: "Activate subscription to buy more" → customer portal
 * - Paid users: "Purchase more"/"Manage credits" → buy-credits page
 */
const CreditButton: React.FC<CreditButtonProps> = ({ style, className }) => {
  const { subscriptionInfo } = useSelector(
    (state: RootState) => state.subscription
  );
  const balanceInfo = useCreditBalance();
  const { currentAbility } = useAbility();
  const navigate = useNavigate();

  const currentTeam = useSelector((state: RootState) => state.team.currentTeam);
  const creditButtonInfo = getCreditButtonInfo({
    ability: currentAbility,
    balanceInfo,
    subscriptionInfo: subscriptionInfo || null,
    currentTeam: currentTeam.record,
  });

  const handleSubscriptionClick = async () => {
    try {
      // Tokens are already available via TokenService (set by AuthenticationGuard)
      // No need to refresh - the auth gate ensures tokens are ready before any API calls
      const subscription = subscriptionInfo?.subscription;
      if (subscription) {
        await openPricingPageOrSubscriptionPortal(subscription.status);
      }
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to open subscription portal'
      );
    }
  };

  const handleClick = () => {
    if (creditButtonInfo.useSubscriptionHandler) {
      handleSubscriptionClick();
    } else {
      // For direct URLs, check if it's a relative path
      const url = creditButtonInfo.url;
      if (url.startsWith('/')) {
        // Navigate within the same window for internal routes
        navigate(url);
      } else {
        // Open external URLs in a new tab
        window.open(url, '_blank');
      }
    }
  };

  return (
    <>
      {!creditButtonInfo.buttonHidden && (
        <Tooltip title={creditButtonInfo.buttonTooltip}>
          <Button
            type="link"
            className={className}
            disabled={creditButtonInfo.buttonDisabled}
            style={{
              padding: 0,
              ...creditButtonInfo.style,
              ...style,
            }}
            onClick={handleClick}
          >
            {creditButtonInfo.text}
          </Button>
        </Tooltip>
      )}
      {creditButtonInfo.instructionForSubscription && (
        <span className="instruction-for-subscription">
          {creditButtonInfo.instructionForSubscription}
        </span>
      )}
    </>
  );
};

export default CreditButton;
